import { closeX } from '../ui/popup.js';
import { createVideoSource } from './video-source.js';
import { beginFrames } from './frame.js';
import { exportName } from './filename.js';
import { renderFrame } from './frame.js';
import { resolveWatermark, stampWatermark } from './png.js';
import { applyAnimation, frameCount, frameDur, settleAt } from '../state/timeline.js';
import { beginVideoPass } from '../scene/video.js';
import { EXPORT_COST, canExportVideoFiles } from '../services/limits.js';
import { track } from '../services/analytics.js';
import { S, clamp, el } from '../state/store.js';
import { toast } from '../ui/toast.js';

/* ═══════════════════════════════════════════════════════════
   EXPORT — the animation, as an MP4
   ═══════════════════════════════════════════════════════════

   Every frame is rendered exactly as the still export renders its one frame
   (export/frame.js), then handed to a hardware video encoder through
   WebCodecs and muxed into an MP4 in memory.

   Why WebCodecs and not MediaRecorder: MediaRecorder captures in real time
   from a live canvas, so a scene that renders at 8fps produces an 8fps
   recording with frames missing, and it writes WebM rather than MP4. This
   renders frame 0, then frame 1, taking as long as each needs, and the
   result is exactly `duration × fps` frames however slow the scene is.

   Mediabunny does the muxing and picks the H.264 profile/level to match the
   frame size — which is not a detail to hand-roll: a fixed codec string like
   `avc1.42001f` is level 3.1 and is REFUSED at 1080p, while `avc1.640028` is
   level 4.0 and is refused at 2160p. Measured in Chrome 151, both ways. */

/* Chrome, Edge and Safari have VideoEncoder; Firefox got it recently. The app
   is desktop-Chromium-only anyway (see ui/mobile-notice.js), but this is a
   feature that can be genuinely absent rather than merely slow, so it is
   worth saying so rather than throwing. */
export const canExportVideo = () => typeof VideoEncoder !== 'undefined';

/* H.264 requires EVEN dimensions in both axes, and S.res accepts any integer
   (ui/rail-left.js) because signage panels are not always even — a 1365-wide
   LED wall is a real thing. Rounding down by one pixel is invisible; failing
   the export at frame 0 with a codec error is not. */
const even = n => Math.max(2, Math.floor(n / 2) * 2);

/** The even size this project's resolution will actually be encoded at. */
export const encodeSize = () => ({ w: even(S.res.w), h: even(S.res.h) });
/* H.264's even-dimension rule applies per panel, and a corner screen's two can
   differ in width — so the pair asks for its own sizes rather than sharing one. */
export const encodeSizeFor = (resW, resH) => ({ w: even(resW), h: even(resH) });
export { subOffset, even };

/* ── motion blur ───────────────────────────────────────────────────────────
   A real camera's shutter is open for a slice of each frame's interval, so
   anything moving is smeared across the distance it travelled while it was
   open. A renderer's "shutter" is instantaneous, which is why fast animation
   rendered frame-by-frame strobes rather than flows.

   The fix is the obvious one and there is no cheaper honest version of it:
   render the frame several times at instants spread across that slice and
   average them. Which means the export does N times the work — the whole
   cost of this feature is that multiplier, it is why it is off by default,
   and it is why estimateSeconds() below multiplies by it rather than leaving
   someone to discover it at frame 40.

   Preview and PNG do not get this. A still has no time slice to integrate
   over, and blurring the working preview would cost the same multiplier on
   every interactive frame. */

export const MOTION_BLUR_STEPS = [1, 2, 4, 8, 16];

/* 180° — a half-open shutter, which is what film cameras standardised on and
   what "natural" motion blur means to anyone who has watched anything. Fixed
   rather than exposed: it is the parameter that changes how the blur LOOKS
   and 0.5 is right almost always, where the sample count is the one that
   changes what it COSTS, which is the trade worth putting in front of
   someone. Widen this if a shutter-angle control is ever wanted. */
const SHUTTER = 0.5;

/* Sample i of n, as a fraction of a frame either side of the frame's own
   instant. Shared by renderBlurred and by the instant list the decode pass is
   built from — if those two ever disagreed, the video would be sampled at
   different moments from everything else in the scene, which is exactly the
   silent one-frame-off defect the pass's own cursor check exists to catch. */
const subOffset = (i, n) => ((i + 0.5) / n - 0.5) * SHUTTER;

/** Sub-frames per output frame, clamped to what the exporter offers. 1 = off. */
export const motionBlurSamples = () =>
  clamp(Math.round(S.motionBlur) || 1, 1, MOTION_BLUR_STEPS[MOTION_BLUR_STEPS.length - 1]);

/**
 * Whether this machine's encoder will take the current output resolution.
 *
 * Asked BEFORE the progress dialog opens, not from inside the render loop —
 * a dialog that appears and immediately replaces itself with an error is a
 * worse way to learn that 8K is too big than simply being told.
 *
 * The profile and level are Mediabunny's problem, and that is the point of
 * letting it decide: a hardcoded codec string is level-locked, and the level
 * has to match the frame size. `avc1.42001f` is level 3.1 and is refused at
 * 1080p; `avc1.640028` is level 4.0 and is refused at 2160p. Measured, both.
 */
export async function canEncodeAt(w, h) {
  if (!canExportVideo()) return false;
  const { canEncodeVideo } = await import('mediabunny');
  return canEncodeVideo('avc', { width: even(w), height: even(h) });
}

/**
 * Render the whole timeline and download it.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.watermark]      force the mark on or off
 * @param {number} [opts.cost]  what a CLEAN copy of this export costs, in
 *        credits. Defaults to a plain video. The on-site mockup is the reason
 *        it is a parameter: it renders through this same loop (see
 *        `opts.paint`) but is priced as its own, cheaper thing, and the
 *        alternative was a second copy of the encoder that could drift.
 * @param {(done:number,total:number)=>void} [opts.onProgress]
 * @param {{aborted:boolean}} [opts.cancel]  set .aborted to stop after the
 *        current frame. Not an AbortSignal: the loop checks between frames
 *        rather than racing a promise, and a plain flag is what that needs.
 */
export async function exportMP4(opts = {}) {
  /* The one thing here that is paid for: a video FILE. Every tool that makes
     one is open to everybody, and a still is free, but this needs a credit
     pack (or a licence) — after which the watermarked file is free and the
     credits take the watermark off. The Export screen never offers it locked;
     this is the backstop, because it is an exported function and "should never"
     is not the same as cannot. */
  const access = canExportVideoFiles();
  if (!access.ok) throw new Error(access.reason);
  if (!canExportVideo())
    throw new Error('This browser cannot encode video. Chrome, Edge or Safari can.');

  const mb = await import('mediabunny');
  const { Output, Mp4OutputFormat, BufferTarget } = mb;

  const fps = S.timeline.fps;
  const total = frameCount();
  /* `opts.paint` lets a caller supply the picture — the on-site mockup does,
     because its frames are a composite over a photograph rather than a
     deliverable. Everything else here is the same for both: the encoder setup,
     the size ceiling, cancellation, progress, and one credit spent up front.
     A second copy of this loop is how the two would drift apart. */
  const { w: outW, h: outH } = opts.size || encodeSize();
  const trimmed = !opts.size && (outW !== S.res.w || outH !== S.res.h);
  const samplesFor = motionBlurSamples();

  if (!(await canEncodeAt(outW, outH)))
    throw new Error(
      `This browser will not encode H.264 at ${outW} × ${outH}. ` +
      'Reduce the output resolution — 4K is usually the ceiling.');

  /* Spent once, before a single frame is rendered — not per frame, and not
     after twenty seconds of work when there is nothing to do about it. */
  const cost = opts.cost ?? EXPORT_COST.video;
  const watermark = await resolveWatermark(opts.watermark, cost);
  /* One seed for the whole export, so the mark's path is settled before the
     first frame and every frame of a block agrees where it is. New each
     export: re-running the same project should not put the mark through the
     same places twice, or the path itself becomes the thing to memorise. */
  const markSeed = (Math.random() * 0x7fffffff) | 0;

  const playheadWas = S.playhead;

  /* Every instant this export will render, in order, before a single frame is
     drawn. A video object's decoder needs the whole list up front — handed a
     lazy one-at-a-time iterator it deadlocks, because nothing reaches its
     sample queue until the key packet changes. It also lets it decode each
     packet at most once for the entire export.

     Strictly increasing even with motion blur on: with SHUTTER 0.5 frame f's
     last sample sits at (f + 0.25)·frameDur and frame f+1's first at
     (f + 0.75)·frameDur, so the samples never overlap between frames. */
  const instants = [];
  for (let f = 0; f < total; f++) {
    if (samplesFor > 1)
      for (let i = 0; i < samplesFor; i++) instants.push((f + subOffset(i, samplesFor)) * frameDur());
    else instants.push(f * frameDur());
  }

  const pass = beginFrames();
  /* One canvas, reused for all of them. Three hundred discarded canvases is
     real memory pressure in the middle of an encode. */
  const canvas = document.createElement('canvas');
  const samples = samplesFor;
  /* A second canvas for the individual sub-frames, so `canvas` can hold the
     running average across them. Only allocated when blur is actually on. */
  const sub = samples > 1 ? document.createElement('canvas') : null;
  if (samples > 1) { canvas.width = outW; canvas.height = outH; }
  /* Before the try below, so a failure here has to end the pass itself: the
     pass holds the live preview (state/bus.js holdRendering) until it ends. */
  let vpass;
  try { vpass = await beginVideoPass(instants); } catch (e) { pass.end(); throw e; }
  let output = null;

  try {
    output = new Output({
      format: new Mp4OutputFormat({
        /* The moov atom goes at the FRONT. Without it a player has to read to
           the end of the file before it can show anything, which is the
           difference between a file that plays instantly off a signage stick
           and one that appears to hang. In memory because the whole thing is
           already in memory — this is not a stream. */
        fastStart: 'in-memory'
      }),
      target: new BufferTarget()
    });

    /* Limited-range BT.709 delivery video, tagged as such — see
       export/video-source.js for why the file is not simply the canvas. Two
       seconds between key frames: signage loops and gets scrubbed by whoever is
       checking it on the wall, and sparser key frames make seeking feel broken
       for a saving nobody asked for. */
    const video = createVideoSource(mb, canvas);
    output.addVideoTrack(video.source, { frameRate: fps });
    await output.start();

    for (let f = 0; f < total; f++) {
      if (opts.cancel?.aborted) throw new CancelledError();

      if (opts.paint) await opts.paint(canvas, f * frameDur());
      else if (samples > 1) await renderBlurred(canvas, sub, f, samples, outW, outH);
      else {
        await settleAt(f * frameDur());
        renderFrame(canvas, outW, outH);
      }
      /* Every frame, not just the first — a mark on frame 0 alone would be a
         watermark you can remove with a trim. It is a canvas composite, so
         the per-frame cost is negligible against the encode. Stamped AFTER
         the sub-frames are averaged, or the mark would be averaged too and
         come out at whatever opacity the arithmetic landed on. */
      if (watermark) await stampWatermark(canvas, null, { seconds: f / fps, seed: markSeed });

      /* Awaited: this is the encoder's own backpressure. Without it the queue
         grows unboundedly and a long export dies on memory rather than
         finishing slowly. */
      await video.add(f / fps, 1 / fps);
      opts.onProgress?.(f + 1, total);
    }

    await output.finalize();
    const blob = new Blob([output.target.buffer], { type: 'video/mp4' });
    // Same rule as the still — see export/png.js.
    download(blob, exportName({ kind: opts.kind || (opts.size ? 'Wall' : ''),
                                spec: `${outW}x${outH}`, fps, ext: 'mp4' }));

    const note = trimmed
      ? ` Sized to ${outW} × ${outH} — H.264 needs even dimensions.`
      : '';
    toast(`Exported ${total} frames at ${fps}fps — ${(total / fps).toFixed(1)} seconds.${note}${pass.note}`,
          !!(note || pass.note));
    track('export_mp4', {
      watermarked: watermark, cost: watermark ? 0 : cost,
      w: outW, h: outH, fps, frames: total, seconds: +(total / fps).toFixed(2)
    });
  } catch (err) {
    /* Cancelling has to leave the output closed, or its buffers stay alive
       for as long as the tab does. */
    try { if (output && output.state === 'started') await output.cancel(); } catch (_) {}
    if (err instanceof CancelledError) { toast('Video export cancelled.'); return; }
    throw err;
  } finally {
    /* Before applyAnimation below, not after: that call takes the preview path
       and would otherwise be asking a generator that is still open for frames
       it was never built for. Runs on the cancel path too, since a
       CancelledError passes through catch and lands here. */
    await vpass.end();
    pass.end();
    /* Put the scene back where the person left it. An export that silently
       moved the playhead to the last frame would look like it broke
       something. */
    applyAnimation(playheadWas);
  }
}

/**
 * Render one output frame as `samples` sub-frames spread across the shutter
 * interval, averaged into `into`.
 *
 * The samples sit at the CENTRES of equal sub-intervals — (i + 0.5)/n rather
 * than i/(n-1) — so the set is a proper box filter over the shutter window
 * with no doubled endpoint, and so a 2-sample blur is two distinct instants
 * rather than the frame time twice.
 *
 * Centred on the frame's own time rather than starting there, which keeps the
 * average instant equal to the frame instant: frame 0 still depicts t=0
 * rather than half a shutter later. Sampling slightly before t=0 costs
 * nothing — evalTrack clamps to the first keyframe, so the value simply
 * holds.
 *
 * The averaging is done by the compositor rather than by summing pixels in
 * JS: drawing sample i at alpha 1/(i+1) over the running result is exactly
 * the arithmetic mean of the samples so far — at i=1 that is half the new
 * frame plus half the first, at i=2 a third of the new plus two thirds of
 * the mean of two, and so on. Which avoids a GPU→CPU readback per sub-frame,
 * at the price of 8-bit rounding between steps that is far below what the
 * encoder will quantise away regardless.
 */
export async function renderBlurred(into, sub, f, samples, outW, outH) {
  const ctx = into.getContext('2d');
  /* A transparent background would otherwise let the PREVIOUS frame show
     through the first sample rather than being replaced by it. */
  ctx.clearRect(0, 0, into.width, into.height);
  for (let i = 0; i < samples; i++) {
    await settleAt((f + subOffset(i, samples)) * frameDur());
    renderFrame(sub, outW, outH);
    ctx.globalAlpha = 1 / (i + 1);
    ctx.drawImage(sub, 0, 0);
  }
  ctx.globalAlpha = 1;
  return into;
}

export class CancelledError extends Error {}

function download(blob, name) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 8000);
}

/* ── the progress dialog ───────────────────────────────────────────────────
   Not a disabled button. Three hundred frames at 1080p is tens of seconds of
   solid work, and a UI that simply stops responding for that long reads as a
   crash — so it says what it is doing, how far along it is, and offers a way
   out. */
export function showMP4Dialog() {
  const wrap = el('div', 'authwrap');
  const card = el('div', 'authcard');
  card.appendChild(el('h2', null, 'Exporting video'));

  const total = frameCount();
  const samples = motionBlurSamples();
  const line = el('p', 'authnote',
    `${total} frames at ${S.timeline.fps}fps — ${(total / S.timeline.fps).toFixed(1)} seconds of video.` +
    /* Said here too, not only before it started: this is the dialog someone
       stares at while it takes four times as long as they remember, and
       "×4 motion blur" is the whole explanation. */
    (samples > 1 ? ` Motion blur is on, so each frame is rendered ${samples} times.` : ''));
  card.appendChild(line);

  const bar = el('div', 'prog');
  const fill = el('div', 'prog__fill');
  bar.appendChild(fill);
  card.appendChild(bar);

  const stat = el('p', 'note', 'Starting…');
  card.appendChild(stat);

  const row = el('div', 'authrow authrow--split');
  const cancel = el('button', 'btn btn--gh', 'Cancel');
  row.appendChild(el('span', 'authnote', ''));
  row.appendChild(cancel);
  card.appendChild(row);
  wrap.appendChild(card);
  document.body.appendChild(wrap);

  const state = { aborted: false };
  cancel.onclick = () => { state.aborted = true; stat.textContent = 'Stopping…'; cancel.disabled = true; };
  closeX(card, () => cancel.click());   // the X stops the export, same as Cancel

  const started = performance.now();
  return {
    cancel: state,
    onProgress(done, all) {
      fill.style.width = (done / all * 100).toFixed(1) + '%';
      const each = (performance.now() - started) / done;
      const left = Math.round((all - done) * each / 1000);
      /* No estimate until there are a few frames to average — a figure
         extrapolated from one frame is wrong by a factor of several and
         changing it every second is worse than not showing one. */
      stat.textContent = done < 5
        ? `Frame ${done} of ${all}`
        : `Frame ${done} of ${all} · about ${left > 60 ? Math.ceil(left / 60) + ' min' : left + 's'} left`;
    },
    close() { wrap.remove(); }
  };
}

/** Render and download, with the dialog. What the button calls. */
export async function exportMP4WithUI(run = exportMP4) {
  const ui = showMP4Dialog();
  try {
    /* `run` so a corner screen can reuse this whole dialog — same progress bar,
       same cancel, same error handling — with its own two-encoder loop. */
    const r = await run({ onProgress: ui.onProgress, cancel: ui.cancel });
    if (r && r.ok === false && r.reason) toast(r.reason, true);
  } catch (err) {
    console.error('[illusio] video export failed', err);
    toast(err.message || 'Video export failed.', true);
  } finally {
    ui.close();
  }
}

/** Roughly how long this will take, for the warning before it starts. Based
 *  on the pixel throughput a mid-range laptop manages — deliberately
 *  pessimistic, because a warning that under-promises is useless. */
export function estimateSeconds() {
  const { w, h } = { w: S.res.w, h: S.res.h };
  const ss = clamp(Math.round(S.ss) || 1, 1, 4);
  const megapixels = (w * h * ss * ss) / 1e6;
  /* Motion blur renders the whole frame once per sample, so it multiplies
     this exactly — it is not a small overhead on top of the render, it IS
     that many more renders. */
  return Math.round(frameCount() * megapixels * 0.02 * motionBlurSamples());
}
